import { cn } from "@alight/ui";

/* =============================================================================
 * LOGO — Connect Sydney
 *
 * The original site's mark is the word CONNECT set in two blues, the two N's
 * overlapping: "CON" in the deep blue, "NECT" in the sky blue. It is rebuilt
 * here as TYPE rather than the ripped JPEG so it scales, sits on any surface
 * and recolours with the theme — the Studio branding mode changes the two
 * tones by editing theme.css alone.
 *
 *   --logo-core   the "CON"   (primary)
 *   --logo-cool   the "NECT"  (secondary)
 * ===========================================================================*/

export type LogoMark = "wordmark";
/** "brand" = the two-tone mark. "mono" = one colour, takes currentColor. */
export type LogoTone = "brand" | "mono";

/** The word on its own — the mark. */
export function LogoMark({
  tone = "brand",
  className,
}: {
  mark?: LogoMark;
  tone?: LogoTone;
  className?: string;
}) {
  return (
    <span
      role="img"
      aria-label="Connect Sydney"
      className={cn(
        "inline-flex select-none items-baseline font-heading text-2xl font-extrabold uppercase leading-none tracking-[-0.04em]",
        className,
      )}
    >
      <span
        style={tone === "brand" ? { color: "var(--logo-core, rgb(var(--color-primary)))" } : undefined}
        className={tone === "mono" ? "text-current" : undefined}
      >
        Con
      </span>
      <span
        style={tone === "brand" ? { color: "var(--logo-cool, rgb(var(--color-secondary)))" } : undefined}
        className={cn("-ml-[0.12em]", tone === "mono" ? "text-current opacity-70" : undefined)}
      >
        nect
      </span>
    </span>
  );
}

/** The full lockup: mark + "Sydney". */
export function Logo({
  tone = "brand",
  className,
  markClassName,
}: {
  mark?: LogoMark;
  tone?: LogoTone;
  className?: string;
  markClassName?: string;
}) {
  return (
    <span className={cn("flex items-center gap-2.5", className)}>
      <LogoMark tone={tone} className={markClassName} />
      <span
        className={cn(
          "mt-0.5 border-l pl-2.5 text-[0.68rem] font-semibold uppercase leading-none tracking-[0.24em]",
          tone === "mono" ? "border-current/40 text-current opacity-80" : "border-border text-muted",
        )}
      >
        Sydney
      </span>
    </span>
  );
}
